import { getOperationName } from '@apollo/client/utilities';
import { useApolloClient } from '@apollo/client';
import { useQuery, useQueryClient } from '@zg-rentals/react-query5';
import { useContext, useEffect, useRef } from 'react';
import { ZgGraphContext } from '../contexts/ZgGraphContext';

/**
 * @param {import('graphql').DocumentNode} query
 * @param {Record} variables
 * @returns {import('@zg-rentals/react-query5').QueryKey}
 */
export function getGraphqlQueryKey(query, variables) {
  return [getOperationName(query), variables];
}
/**
 * Helper hook to allow calling a graphql query using react-query while maintaining normalized cache
 * @param {import('@apollo/client').ApolloClient} apolloClient
 * @param {import('graphql').DocumentNode} query
 * @param {Record} variables
 * @param {import('@zg-rentals/react-query5').UseQueryOptions} queryOptions
 * @returns {import('@zg-rentals/react-query5').UseQueryResult}
 */
export function useGraphqlQuery(apolloClient, query, variables = {}, queryOptions = {}) {
  const queryClient = useQueryClient();
  const observableQueryRef = useRef(
    apolloClient.watchQuery({
      query,
      variables,
      fetchPolicy: 'cache-only',
    }),
  );

  const queryKey = getGraphqlQueryKey(query, variables);

  useEffect(() => {
    observableQueryRef.current = apolloClient.watchQuery({
      query,
      variables,
      fetchPolicy: 'cache-only',
    });
    const subscription = observableQueryRef.current.subscribe((result) => {
      if (!result.loading && !result.partial) {
        queryClient.setQueryData(queryKey, result.data);
      }
    });

    return () => subscription.unsubscribe();
  }, [apolloClient, queryClient, JSON.stringify(queryKey)]);

  return useQuery({
    queryKey,
    queryFn: async () => {
      const { data, error } = await observableQueryRef.current.refetch(variables);
      if (error) {
        throw error;
      }

      return data;
    },
    ...queryOptions,
  });
}

/**
 * @param {import('graphql').DocumentNode} query
 * @param {Record} variables
 * @param {import('@zg-rentals/react-query5').UseQueryOptions} queryOptions
 * @returns {import('@zg-rentals/react-query5').UseQueryResult}
 */
export default function useAlwGraphqlQuery(query, variables = {}, queryOptions = {}) {
  const apolloClient = useApolloClient();
  return useGraphqlQuery(apolloClient, query, variables, queryOptions);
}

/**
 * @param {import('graphql').DocumentNode} query
 * @param {Record} variables
 * @param {import('@zg-rentals/react-query5').UseQueryOptions} queryOptions
 * @returns {import('@zg-rentals/react-query5').UseQueryResult}
 */
export function useZgGraphqlQuery(query, variables = {}, queryOptions = {}) {
  const apolloClient = useContext(ZgGraphContext).client;
  return useGraphqlQuery(apolloClient, query, variables, queryOptions);
}
